/* eslint-disable @ngrx/avoid-dispatching-multiple-actions-sequentially */
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationStart,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { createSelector, Store } from '@ngrx/store';
import { THDSAngularAuthService } from '@thds/auth/angular';
import { SidebarModule } from '@thds/components/sidebar';
import { enableMapSet } from 'immer';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilKeyChanged,
  filter,
  map,
  of,
  skipWhile,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { MitarbeiterDto } from './api/models';
import { BuchungsaenderungsAntraegeStore } from './buchungsaenderungs-antraege-store.service';
import { BuchungserstellAntraegeStore } from './buchungserstell-antraege-store.service';
import { featureFlagEnabled } from './feature-flag';
import { AuthActions } from './store/auth/auth.actions';
import * as fromAuth from './store/auth/auth.selectors';
import { ChipsActions } from './store/chips/chips.actions';
import { MitarbeiterActions } from './store/mitarbeiter/mitarbeiter.actions';
import * as TagesplaeneActions from './store/tagesplaene/tagesplaene.actions';
import { UrlaubsantraegeActions } from './store/urlaubsantraege/urlaubsantraege.actions';
import * as fromUrlaubsantraege from './store/urlaubsantraege/urlaubsantraege.selectors';
import * as WochenplaeneActions from './store/wochenplaene/wochenplaene.actions';

const selectVm = createSelector(
  fromAuth.selectHasAnyScope(
    'buchungsAenderungsAntrag:read:all',
    'buchungsAenderungsAntrag:read:unit:lead',
    'buchungsAenderungsAntrag:read:unit:own',
  ),
  fromAuth.selectHasAnyScope(
    'buchungsErstellAntrag:read:all',
    'buchungsErstellAntrag:read:unit:lead',
    'buchungsErstellAntrag:read:unit:own',
  ),
  fromAuth.selectUserScopes,
  fromAuth.selectLoggedInMitarbeiter,
  (
    aenderungsAntraegeReadAllOrUnit,
    erstellAntraegeReadAllOrUnit,
    scopes,
    mitarbeiter,
  ) => ({
    aenderungsAntraegeReadAllOrUnit,
    erstellAntraegeReadAllOrUnit,
    scopes,
    mitarbeiter,
  }),
);

@Component({
  selector: 'thds-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [
    SidebarModule,
    IonicModule,
    RouterLink,
    CommonModule,
    RouterLinkActive,
  ],
})
export class AppComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly _router = inject(Router);
  readonly authService = inject(THDSAngularAuthService);
  //instantiating for sses DO NOT REMOVE FOLLOWING TWO LINES

  readonly $isLoggedIn = this.authService.$isLoggedIn.pipe(
    skipWhile((val) => val === null),
  );

  private readonly authData$ = this.store.select(fromAuth.selectUserWithScopes);
  userScopes: string[] = [];
  currentUrl$ = new BehaviorSubject<string>('');
  destroyref$ = inject(DestroyRef);

  countOpenUrlaubsantraege$ = this.store.select(
    fromUrlaubsantraege.selectOpenUrlaubsantraegeCount,
  );

  public readonly vm$ = this.store.select(selectVm);

  private readonly _aenderungsanfragenStore = inject(
    BuchungsaenderungsAntraegeStore,
  );

  private readonly _erstellAntraegeStore = inject(BuchungserstellAntraegeStore);

  countOpenAenderungsAntraegeWithoutMonteure$ = this.vm$.pipe(
    filter((vm) => vm.mitarbeiter !== null),
    takeUntilDestroyed(this.destroyref$),
    distinctUntilKeyChanged('scopes'),
    skipWhile(({ scopes }) => scopes === null),
    switchMap((vm) => {
      if (vm.aenderungsAntraegeReadAllOrUnit)
        return this._aenderungsanfragenStore
          .countOpenAenderungsAntraegeWithoutMonteure$;
      else return of(0);
    }),
  );

  countOpenAenderungsAntraegeMonteure$ = this.vm$.pipe(
    filter((vm) => vm.mitarbeiter !== null),
    takeUntilDestroyed(this.destroyref$),
    distinctUntilKeyChanged('scopes'),
    skipWhile(({ scopes }) => scopes === null),
    switchMap((vm) => {
      if (vm.aenderungsAntraegeReadAllOrUnit)
        return this._aenderungsanfragenStore
          .countOpenAenderungsAntraegeMonteure$;
      else return of(0);
    }),
  );

  countOpenErstellAntraegeWithoutMonteure$ = this.vm$.pipe(
    filter((vm) => vm.mitarbeiter !== null),
    takeUntilDestroyed(this.destroyref$),
    distinctUntilKeyChanged('scopes'),
    skipWhile(({ scopes }) => scopes === null),
    switchMap((vm) => {
      if (vm.erstellAntraegeReadAllOrUnit)
        return this._erstellAntraegeStore
          .countOpenErstellAntraegeWithoutMonteure$;
      else return of(0);
    }),
  );

  countOpenErstellAntraegeMonteure$ = this.vm$.pipe(
    filter((vm) => vm.mitarbeiter !== null),
    takeUntilDestroyed(this.destroyref$),
    distinctUntilKeyChanged('scopes'),
    skipWhile(({ scopes }) => scopes === null),
    switchMap((vm) => {
      if (vm.erstellAntraegeReadAllOrUnit)
        return this._erstellAntraegeStore.countOpenErstellAntraegeMonteure$;
      else return of(0);
    }),
  );

  countOpenMonteureAntraege$ = combineLatest([
    this.countOpenErstellAntraegeMonteure$.pipe(startWith(0)),
    this.countOpenAenderungsAntraegeMonteure$.pipe(startWith(0)),
  ]).pipe(map(([x, y]) => x + y));
  countOpenWithoutMonteureAntraege$ = combineLatest([
    this.countOpenAenderungsAntraegeWithoutMonteure$.pipe(startWith(0)),
    this.countOpenErstellAntraegeWithoutMonteure$.pipe(startWith(0)),
  ]).pipe(map(([x, y]) => x + y));

  ngOnInit(): void {
    enableMapSet();

    if (featureFlagEnabled('clock-in-base')) {
      this.store.dispatch(WochenplaeneActions.loadActiveWochenplaene());
      this.store.dispatch(TagesplaeneActions.loadActiveTagesplaene());
      this.store.dispatch(ChipsActions.loadAvailableChips());
      if (this.hasScope('zutrittskontrolle:verwaltung:all')) {
        this.store.dispatch(ChipsActions.loadAllChips());
      }

      this.authService.$isLoggedIn
        .pipe(
          filter((x) => x !== null),
          tap(() => {
            this.store.dispatch(
              AuthActions.setLoggedInUser({
                userId: this.authService.userID ?? null,
                scopes: this.authService.userScopes,
              }),
            );
          }),
        )
        .subscribe();
    }
    // Fuer ActiveLink Fix Sidebar
    this._router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyref$),
      )
      .subscribe((e) => {
        const pathOnly = e.url.split('?')[0].trim();
        this.currentUrl$.next(pathOnly);
      });

    this.authData$
      .pipe(
        filter(
          (data): data is { user: MitarbeiterDto; scopes: string[] } =>
            data.user !== null,
        ),
        distinctUntilKeyChanged('scopes'),
        skipWhile((scopes) => scopes === null),
        tap(({ user, scopes }) => {
          this.userScopes = scopes;
          const unitOwnScope = scopes.includes('urlaubsantraege:read:unit:own');
          const unitLeadScope = scopes.includes(
            'urlaubsantraege:read:unit:lead',
          );
          if (scopes.includes('urlaubsantraege:read:all')) {
            this.store.dispatch(
              MitarbeiterActions.setUnitFilter({ units: null }),
            );
            this.store.dispatch(
              UrlaubsantraegeActions.loadOpenUrlaubsantraegeCount(),
            );
            return this.store.dispatch(
              UrlaubsantraegeActions.loadAllUrlaubsantraege(),
            );
          }
          if (unitLeadScope || unitOwnScope) {
            const units: string[] = [];
            if (unitLeadScope) {
              units.push(...user.geleiteteAbteilungen.map((x) => x.name));
            }
            if (unitOwnScope) {
              units.push(user.abteilung);
            }
            this.store.dispatch(MitarbeiterActions.setUnitFilter({ units }));
            this.store.dispatch(
              UrlaubsantraegeActions.loadOpenUrlaubsantraegeCount(),
            );
            return this.store.dispatch(
              UrlaubsantraegeActions.loadUnitUrlaubsantraege(),
            );
          }
        }),
        takeUntilDestroyed(this.destroyref$),
      )
      .subscribe();
  }

  hasScope(scope: string) {
    return this.authService.userScopes.includes(scope);
  }

  onLogout(): void {
    this.authService.logoutUser();
    location.reload();
  }
}
